<template>
  <CModal
      :show.sync="visible"
      size="lg"
      :title="modalTitle"
      @hidden="onHidden"
      centered
      :close-on-backdrop="false"
  >

    <template #header>
      <div>
        <div class="d-flex align-items-center">
          <CIcon name="cipCopy" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">
              <translate translate-context="collaborators.virtualusers">Credentials</translate>
            </h5>
          </div>
        </div>
      </div>
      <CButtonClose @click="close"/>
    </template>

    <!-- Warning Banner -->
    <PAlert color="warning" class="mb-4" icon="cipAlertTriangle">
        <div>
          <div class="font-weight-semi-bold">
            <translate translate-context="collaborators.virtualusers">
              Save credentials
            </translate>
          </div>
          <div class="mt-1">
            <translate translate-context="collaborators.virtualusers">
              They will NOT be shown again. Make sure to copy and save them securely.
            </translate>
          </div>
        </div>
    </PAlert>

    <!-- Single Credential Display -->
    <div v-if="!isMultiple && credentials" class="credentials-display p-3">
      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Email</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.email" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="copyField(credentials.email)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Password</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.password" :type="showPassword ? 'text' : 'password'" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="showPassword = !showPassword">
              <CIcon :name="showPassword ? 'cilEyeSlash' : 'cilEye'"/>
            </CButton>
            <CButton color="secondary" @click="copyField(credentials.password)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Domain</translate>
        </CLabel>
        <CInput :value="credentials.domain" readonly/>
      </CFormGroup>
    </div>

    <!-- Multiple Credentials Display (Bulk Reset) -->
    <div v-if="isMultiple && credentialsArray.length > 0" class="credentials-table">
      <CDataTable
          :items="credentialsArray"
          :fields="tableFields"
      >
        <template #password="{item}">
          <td style="border-bottom: 1px solid #ddd;">
            <div class="d-inline-block font-weight-medium" style="background: #F7F4FF;padding: 4px 6px;border-radius: 6px">{{ item.password }}</div>
          </td>
        </template>
        <template #email="{item}">
          <td>
            <span>{{ item.email }}</span>
          </td>
        </template>
      </CDataTable>
    </div>

    <!-- Copy All Section -->
    <div class="mt-4 p-3" style="background-color: #FAFAFA;border-radius: 12px ">
      <div class="d-flex justify-content-between align-items-center">
        <span class="font-weight-medium">
          <translate translate-context="collaborators.virtualusers">
            {{ isMultiple ? 'Copy All Credentials' : 'Copy Credentials' }}
          </translate>
        </span>
        <CButton color="light" size="sm" @click="copyAll" style="background-color: #fff; border: 1px solid #ddd;">
          <CIcon name="cipCopy" class="mr-2"/>
          <translate translate-context="collaborators.virtualusers">Copy All</translate>
        </CButton>
      </div>
      <div class="" style="color: #8887A0">
        <translate translate-context="collaborators.virtualusers">
          Formatted text ready to share via WhatsApp or secure messaging
        </translate>
      </div>
    </div>

    <template #footer>
      <CButton color="primary" @click="close">
        <translate translate-context="collaborators.virtualusers">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapGetters} from 'vuex'
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'VirtualUserCredentialsModal',
  components: {PAlert},

  data() {
    return {
      visible: false,
      credentials: null,
      showPassword: false,
      tableFields: [
        {
          key: 'email',
          label: this.$pgettext('collaborators.virtualusers', 'Email')
        },
        {
          key: 'password',
          label: this.$pgettext('collaborators.virtualusers', 'Password')
        }
      ]
    }
  },

  computed: {
    ...mapGetters('event', [
      'currentEventName'
    ]),

    isMultiple() {
      return Array.isArray(this.credentials)
    },

    credentialsArray() {
      return this.isMultiple ? this.credentials : []
    },

    modalTitle() {
      return this.$pgettext('collaborators.virtualusers', 'Virtual User Credentials')
    }
  },

  methods: {
    show(credentials) {
      this.credentials = credentials
      this.visible = true
      this.showPassword = false
    },

    close() {
      this.visible = false
    },

    onHidden() {
      // Clear credentials from memory when modal closes
      this.credentials = null
      this.showPassword = false

      // Clear from Vuex store
      this.$store.dispatch('collaborators/clearVirtualUserCredentials')
    },

    copyField(text) {
      this.copyToClipboard(text)
      this.notifySuccess(
          this.$pgettext('collaborators.virtualusers', 'Copied to clipboard')
      )
    },

    copyAll() {
      const text = this.isMultiple
          ? this.formatMultipleCredentials()
          : this.formatSingleCredential()

      this.copyToClipboard(text)
      this.notifySuccess(
          this.$pgettext('collaborators.virtualusers', 'All credentials copied to clipboard')
      )
    },

    formatSingleCredential() {
      if (!this.credentials) return ''

      const serviceName = this.currentEventName || 'Virtual User'

      return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Email: ${this.credentials.email}
Password: ${this.credentials.password}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep this password secure
`.trim()
    },

    formatMultipleCredentials() {
      if (!this.credentialsArray.length) return ''

      const serviceName = this.currentEventName || 'Virtual Users'
      let text = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

`
      this.credentialsArray.forEach((cred) => {
        text += `Email: ${cred.email}
Password: ${cred.password}

`
      })

      text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep these passwords secure
`
      return text.trim()
    },

    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style scoped>
.credentials-display {
  background-color: #FAFAFA;
  border-radius: 12px
}


.credentials-table td{
 border-bottom: 1px solid #E1DFFB !important;
}
.credentials-table tr:last-child td{
 border-bottom: none !important;
}

code.text-monospace {
  background-color: #e9ecef;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>
